import { IonContent, IonHeader, IonInput, IonPage } from "@ionic/react";
import { useContext, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import CustomTable from "../components/CustomTable";
import ResultsText from "../components/ResultsText";
import Toolbar from "../components/Toolbar";
import AppContext from "../providers/AppContext";
import haversine from "haversine-distance";
import { LocationCluster, Report } from "../utils/Interfaces";
import { UtilFunctions } from "../utils/UtilFunctions";
import RouteText from "../components/RouteText";
import Footer from "../components/Footer";
import TableInstructionText from "../components/TableInstructionText";

const Locations: React.FC<RouteComponentProps> = props => {
  const appContext = useContext(AppContext);

  const [locations, setLocations] = useState<LocationCluster[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<LocationCluster[]>([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (appContext.jwt === "") {
      // browser refreshed so move back to landing
      props.history.replace("/");
    }
  }, []);

  useEffect(() => {
    // the app sets lat and lng values to zero if location permission is NOT given
    const filteredReports = appContext.reports.filter(rep => !!rep.latitude && !!rep.longitude);

    const localAuthGroups = groupBy(filteredReports, "localAuth");

    const locationClusters: LocationCluster[] = [];
    localAuthGroups.forEach(grpArr => {
      let searchList = grpArr;
      while (searchList.length != 0) {
        const originReport = searchList[0];
        if (searchList.length === 1) {
          // add a single report
          locationClusters.push({
            originName: originReport.carWashName,
            reportIds: [originReport.id],
            count: 1,
            firstDate: originReport.dateTimeCreated,
            lastDate: originReport.dateTimeCreated,
          });
          searchList = [];
        } else {
          // add multiple reports that are within 100m of an origin
          const newArr = searchList.slice(1).filter(item => {
            const differece = haversine(
              { latitude: originReport.latitude, longitude: originReport.longitude },
              { latitude: item.latitude, longitude: item.longitude }
            );
            return differece <= 100;
          });
          // add origin to beginning of new array
          newArr.unshift(originReport);
          locationClusters.push({
            originName: originReport.carWashName,
            reportIds: newArr.map(item => item.id),
            count: newArr.length,
            firstDate: getEarliestDateFromReports(newArr),
            lastDate: getLastDateFromReports(newArr),
          });
          // change search list
          searchList = searchList.filter(item => {
            const differece = haversine(
              { latitude: originReport.latitude, longitude: originReport.longitude },
              { latitude: item.latitude, longitude: item.longitude }
            );
            return !(differece <= 100);
          });
        }
      }
    });
    setFilteredLocations(locationClusters);
    setLocations(locationClusters);
  }, []);

  function handleSearch(searchText: string) {
    setSearchText(searchText);
    let filteredList = [];
    const text = searchText.toLowerCase().trim();
    if (!text || text === "") {
      // reapply filters without search text
      filteredList = locations;
    } else {
      // filter list
      filteredList = locations.filter((value, index, array) => {
        // properties to search through
        const filterArray = [value.originName];
        return filterArray.some(value => value.includes(text));
      });
    }
    setFilteredLocations(filteredList);
  }

  function getEarliestDateFromReports(reps: Report[]) {
    let earliestDate: Date = new Date(); // init to today
    reps.forEach((value, index, array) => {
      if (value.dateTimeCreated < earliestDate) {
        earliestDate = value.dateTimeCreated;
      }
    });
    return earliestDate;
  }

  function getLastDateFromReports(reps: Report[]) {
    let lastDate: Date = getEarliestDateFromReports(reps); // init to earliest date
    reps.forEach((value, index, array) => {
      if (value.dateTimeCreated > lastDate) {
        lastDate = value.dateTimeCreated;
      }
    });
    return lastDate;
  }

  function groupBy(collection: any[], property: string): Report[][] {
    var i = 0,
      val,
      index,
      values = [],
      result = [];
    for (; i < collection.length; i++) {
      val = collection[i][property];
      index = values.indexOf(val);
      if (index > -1) result[index].push(collection[i]);
      else {
        values.push(val);
        result.push([collection[i]]);
      }
    }
    return result as Report[][];
  }

  return (
    <IonPage>
      <IonHeader>
        <Toolbar props={props} />
      </IonHeader>
      <IonContent>
        <div className="pageContainer">
          <div className="pageContentContainer">
            <RouteText routes={[{ name: "Dashboard", route: "/" }]} currentLocation="Locations" routeProps={props} />
            <h1 className="pageHeader">Car Wash Locations</h1>
            <div style={{ marginTop: "24px", marginBottom: "16px" }}>
              <p id="searchBarTitle">Search</p>
              <IonInput value={searchText} onIonChange={e => handleSearch(e.detail.value!)} />
            </div>
            <div className="numberOfResultsContainer">
              <ResultsText count={filteredLocations.length} />
            </div>
            <CustomTable
              rowData={filteredLocations}
              cols={[
                {
                  Header: "Origin Car Wash",
                  accessor: "originName",
                  Cell: (props: any) => {
                    return <div>{props.value}</div>;
                  },
                },
                {
                  Header: "First Date",
                  accessor: "firstDate",
                  Cell: (props: any) => {
                    return <div>{UtilFunctions.formatDate(props.value)}</div>;
                  },
                },
                {
                  Header: "Last Date",
                  accessor: "lastDate",
                  Cell: (props: any) => {
                    return <div>{UtilFunctions.formatDate(props.value)}</div>;
                  },
                },
                {
                  Header: "Count",
                  accessor: "count",
                  Cell: (props: any) => {
                    return <div>{props.value}</div>;
                  },
                },
              ]}
              handleSelectItem={item => {
                appContext.setSelectedLocationCluster(item);
                props.history.push("/individual-locations");
              }}
              defaultSort={[{ id: "count", desc: true }]}
            />
            <TableInstructionText text="Search input looks for matches to origin car wash." />
          </div>
          <Footer />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Locations;
